<template>
  <div v-if="isLoading">
    <payout-details-loader />
  </div>
  <main v-else class="md:mx-14 mx-4 pt-8">
    <div>
      <div class="flex items-center gap-x-4 lg:gap-x-12">
        <div
          @click="goBack"
          class="flex items-center border-main rounded-lg shadow-xs w-20 gap-x-2 text-gray-90 border-gray-90 px-1 py-1.5 lg:py-2 font-semibold text-fg-default text-xs cursor-pointer justify-center"
        >
          <i class="el-icon-back"></i>
          <p class="font-light text-xs">Back</p>
        </div>
        <div class="flex items-center gap-x-2 lg:gap-x-4">
          <bulk-payout-icon-2 />
          <span class="text-lg font-light text-gray-muted">></span>
          <p class="font-normal text-xs lg:text-sm capitalize">
            {{ bulkPayoutDetails.remarks || '-' }}
          </p>
        </div>
      </div>

      <div
        v-if="isDisapprovedStatus"
        class="flex items-center gap-2 font-normal mt-8 bg-muted px-4 py-3 text-sm text-gray-muted"
      >
        <alert-icon color="#A5A7B6" />
        <p>Termination Reason: {{ bulkPayoutDetails.cancelReason }}</p>
      </div>

      <div>
        <div class="my-4" v-if="isBulkPayoutCancelled && !isLogTableView">
          <p class="bg-info-subtle px-3 py-4 rounded-sm shadow-xs w-full">
            <span
              class="font-light lg:text-sm text-xs"
              style="word-break: normal"
              >Termination Reason: {{ bulkPayoutDetails.cancelReason }}</span
            >
          </p>
        </div>
        <div v-if="isRecurringPayoutFailed">
          <p
            class="bg-muted flex gap-x-1 items-center px-3 py-4 mt-3 rounded-sm shadow-xs w-[85%]"
          >
            <span>
              <info-icon />
            </span>
            <span
              class="font-light normal-case lg:text-sm text-xs"
              style="word-break: normal"
            >
              {{ recurringLogDetails.reason }}</span
            >
          </p>
        </div>
      </div>

      <div
        class="mt-8 flex flex-col gap-6 sm:flex-row sm:gap-0 sm:items-center sm:justify-between"
      >
        <div class="sm:w-1/3">
          <div class="flex flex-col gap-x-2">
            <div class="flex items-center gap-3">
              <p class="text-sm text-gray-muted font-normal capitalize">
                Name: {{ bulkPayoutDetails.remarks || '-' }}
              </p>

              <el-tooltip
                v-if="isRecurringPayout"
                content="This is a Recurring Payout"
                placement="top"
              >
                <span
                  class="h-5 w-8 flex items-center justify-center rounded-2xl bg-gray-26"
                >
                  <recurring-icon size="h-3 w-3" />
                </span>
              </el-tooltip>
            </div>

            <div class="flex items-center gap-5 mt-4">
              <span
                class="inline-flex items-center gap-x-2 border border-gray-120 rounded-full px-1"
              >
                <currency-icons
                  size="h-4 w-4"
                  :currency="
                    getCurrencyCountry(
                      bulkPayoutDetails.currency || $CURRENCY.NGN
                    )
                  "
                />
                <span class="text-sm font-bold text-fg-default">{{
                  bulkPayoutDetails.currency || $CURRENCY.NGN
                }}</span>
              </span>

              <p class="text-lg sm:text-2xl font-bold">
                {{
                  parseMoneyAll(
                    bulkPayoutDetails.totalAmount || 0,
                    bulkPayoutDetails.currency || $CURRENCY.NGN
                  )
                }}
              </p>

              <el-tooltip
                :content="`Fees - ${parseMoneyAll(
                  bulkPayoutDetails.fee.amount,
                  bulkPayoutDetails.fee.currency
                )}`"
                placement="top"
              >
                <span class="cursor-pointer">
                  <info-icon size="h-5 w-5" />
                </span>
              </el-tooltip>
            </div>
          </div>
          <div class="flex items-center gap-3 mt-2">
            <p class="text-xs lg:text-sm font-medium text-black-10">
              Payout ID:
            </p>
            <span class="text-xs lg:text-sm font-medium text-black-10">
              {{ maskID(bulkPayoutDetails.id || '-') }}
            </span>
            <span
              class="cursor-pointer"
              v-clipboard:copy="bulkPayoutDetails.id || '-'"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <p class="text-sm text-gray-muted font-normal capitalize">Status</p>
          <span
            class="w-max inline-block text-xs font-semibold capitalize px-4 py-2 rounded-2xl mt-2"
            :class="statusClass(bulkPayoutDetails.status || $STATUS.INITIATED)"
          >
            {{
              formatPayoutStatus(bulkPayoutDetails.status) || $STATUS.INITIATED
            }}
          </span>
        </div>

        <div class="flex flex-col gap-1">
          <p class="text-sm text-gray-muted font-normal capitalize">
            Date Created
          </p>
          <span class="text-sm font-semibold mt-2 text-black-1 capitalize">
            {{ parseRelativeTime(bulkPayoutDetails.createdAt) }}
          </span>
        </div>

        <div class="flex items-center gap-x-2">
          <div
            v-if="isRecurringPayoutFailed && !isBulkPayoutCancelled"
            @click="confirmRetryPayoutHandler"
            class="px-4 py-2 lg:py-3.5 w-2/4 lg:w-full rounded-lg shadow-xs border border-foreground flex gap-x-2 cursor-pointer items-center"
          >
            <check-ring />
            <span class="text-foreground font-normal text-xs lg:text-base"
              >Retry Payout</span
            >
          </div>

          <div
            v-if="isCancelStatus"
            @click="confirmTerminateHandler('terminateAfterApproval')"
            class="px-4 py-3.5 rounded-lg shadow-xs border border-red-600 cursor-pointer flex gap-x-2 items-center"
          >
            <close-icon-2 />
            <span class="text-red-600 font-normal text-xs lg:text-base"
              >Terminate</span
            >
          </div>
        </div>

        <div v-if="isSingleCompletedPayout" class="gap-x-2">
          <button
            class="flex gap-x-2 bg-green-6 text-green-1 border border-green-1 py-4 rounded-lg px-4"
            @click="generateReceipt"
          >
            <download-icon />
            {{ isLoading ? 'Downloading...' : 'Download Receipt' }}
          </button>
        </div>

        <div v-if="isAwaitingApprovalStatus" class="flex items-center gap-x-3">
          <el-tooltip
            :content="approveTooltipContent"
            placement="top"
            :disabled="isApproveTooltipDisabled"
          >
            <button
              @click="confirmApprovePayoutHandler"
              :disabled="isApproverAccess"
              class="px-4 py-3.5 rounded-lg shadow-xs outline-none flex gap-x-2 items-center hover:opacity-70 approveBtn"
            >
              <check-ring v-if="isApproverAccess" color="#A5A7B6" />
              <check-ring v-else />
              <span class="font-normal text-xs lg:text-base"
                >Approve Payout</span
              >
            </button>
          </el-tooltip>

          <el-tooltip
            content="You do not have the access to terminate this payout"
            placement="top"
            :disabled="hasApproverAccess"
          >
            <button
              @click="confirmTerminateHandler('terminateBeforeApproval')"
              :disabled="!hasApproverAccess"
              class="px-4 py-3.5 rounded-lg shadow-xs outline-none flex gap-x-2 items-center hover:opacity-70 terminateBtn"
            >
              <close-icon-2 v-if="hasApproverAccess" />
              <close-icon-2 v-else color="#A5A7B6" />
              <span class="font-normal text-xs lg:text-base">Terminate</span>
            </button>
          </el-tooltip>
        </div>
      </div>

      <div>
        <initiator-details
          v-if="hasInitiatorDetails"
          :is-singleBulk-payout="isSingleBulkPayout"
          :is-multipleBulk-payout="isMultipleBulkPayout"
          :is-awaiting-approval-status="isAwaitingApprovalStatus"
          :transaction-details="transactionInitiatorDetails"
        />

        <hr v-else class="my-8" />
      </div>
    </div>

    <!-- beneficiary table -->
    <beneficiary-table
      v-if="showBeneficiaryTable"
      :isLoading="isLoadingTable"
      :table-data="beneficiaryTableData"
      table-type="history"
      :page="page"
      @sort="sortHandler"
      @handleCurrentChange="handleCurrentChange"
      @refresh="refreshHandler"
    />

    <!-- Beneficiary Details -->
    <div v-if="showBeneficiaryDetails">
      <h1 class="font-bold text-black-10 text-xl">Beneficiary Details</h1>
      <div class="bg-gray-30 mt-5 py-8 px-11 rounded-lg">
        <div class="bg-white rounded-lg px-8 pt-8 flex flex-wrap">
          <div
            v-for="(beneficiary, index) in singleBeneficiaryDetails"
            :key="index"
            :class="{ 'w-1/2 sm:w-1/3 2xl:w-1/4': beneficiary.value }"
          >
            <div v-if="beneficiary.value" class="w-full mb-11">
              <p class="font-bold text-black-10 text-13px mb-2">
                {{ beneficiary.title }}
              </p>
              <p class="font-normal text-sm text-black-10">
                {{ beneficiary.value || '-' }}
              </p>
            </div>
          </div>
        </div>

        <!-- intermediary bank details -->
        <div
          v-if="hasIntermediaryBankDetails"
          class="bg-white rounded-lg mt-5 px-8 pt-8"
        >
          <p class="font-normal text-sm lg:text-lg pb-4">
            Intermediary Bank Details
          </p>
          <div class="flex flex-wrap w-full">
            <div
              v-for="(details, index) in intermediaryBankDetailsStruct"
              :key="index"
              :class="{ 'w-1/2 sm:w-1/3 2xl:w-1/4': details.value }"
            >
              <div v-if="details.value" class="w-full mb-11">
                <p class="font-bold text-black-10 text-13px mb-2">
                  {{ details.title }}
                </p>
                <p class="font-normal text-sm text-black-10 capitalize">
                  {{ details.value || '-' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- recurring payout logs -->
    <recurring-payout-log-table
      v-if="showRecurringLogTable"
      :table-data="bulkPayoutLogTableData"
      :isLoading="isLoading"
      :page="bulkPayoutLogPage"
      @toggleTableView="toggleTableView"
      @handleCurrentChange="handleRecurringLogCurrentChange"
    />

    <!-- Drawer -->
    <initiator-details-drawer
      v-if="showInitiatorDrawer"
      :showDrawer="showInitiatorDrawer"
      :details="transactionInitiatorDetails"
      @closeDrawer="closeInitiatorDrawer"
    />

    <!-- dialogs -->
    <confirm-payout
      v-if="showConfirmPayout"
      :showDialog="showConfirmPayout"
      @closeDialog="closeDialog('showConfirmPayout')"
      @handleConfirmBulkPayout="handleConfirmBulkPayout"
    />
    <confirm-terminate
      v-if="showConfirmTerminate"
      :showDialog="showConfirmTerminate"
      @closeDialog="closeDialog('showConfirmTerminate')"
      @handleTerminateBulkPayout="handleTerminateBulkPayout"
    />
    <confirm-retry
      v-if="showConfirmRetry"
      :showDialog="showConfirmRetry"
      @closeDialog="closeDialog('showConfirmRetry')"
      @handleRetryBulkPayout="handleRetryBulkPayout"
    />
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeDialog('showVerifyAuthDialog')"
      @terminateBulkPayout="terminateBulkPayout"
      @approveBulkPayout="approveBulkPayout"
      @confirmBulkPayout="confirmBulkPayoutHandler"
      @retryBulkPayout="retryBulkPayoutHandler"
    />

    <!-- PDF Receipt -->
    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <generate-receipt-details :transferType="'Payout'" />
      </section>
    </vuehtml2pdf>
  </main>
</template>

<script>
import {
  GetCurrencyCountry,
  ParseMoneyAll,
  ParseRelativeTime,
  StatusClass,
  MaskID
} from '@/utils/helpers'
import { IS_RECURRING_PAYOUT_RETRY_STATUS } from '@/utils/constants'
import formatPayoutStatus from '@/mixins/transactions.js'
import userKinds from '@/mixins/userKinds.js'
import app from '@/mixins/app.js'
import getReceiptDetails from '@/mixins/global.js'

export default {
  mixins: [formatPayoutStatus, userKinds, app, getReceiptDetails],
  components: {
    BulkPayoutIcon2: () => import('@/assets/v2/icons/BulkPayoutIcon2.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    RecurringIcon: () => import('@/assets/v2/icons/RecurringIcon.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    InfoIcon: () => import('@/assets/v2/icons/InfoIcon.vue'),
    AlertIcon: () => import('@/assets/v2/icons/InfoTwoIcon.vue'),
    CheckRing: () => import('@/assets/v2/icons/CheckRing.vue'),
    CloseIcon2: () => import('@/assets/v2/icons/CloseIcon2.vue'),
    DownloadIcon: () => import('@/assets/v2/icons/CloudDownloadIcon.vue'),
    BeneficiaryTable: () =>
      import('@/components/v2/bulkPayout/tables/BeneficiaryTable.vue'),
    RecurringPayoutLogTable: () =>
      import('@/components/v2/bulkPayout/tables/RecurringPayoutLogTable.vue'),
    ConfirmTerminate: () =>
      import('@/components/v2/bulkPayout/dialogs/ConfirmTerminate.vue'),
    ConfirmRetry: () =>
      import('@/components/v2/bulkPayout/dialogs/ConfirmRetry.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog.vue'),
    ConfirmPayout: () =>
      import('@/components/v2/bulkPayout/dialogs/ConfirmPayout.vue'),
    InitiatorDetails: () =>
      import(
        '@/components/v2/bulkPayout/layouts/payoutDetails/InitiatorDetails.vue'
      ),
    InitiatorDetailsDrawer: () =>
      import('@/components/v2/bulkPayout/drawers/InitiatorDetailsDrawer.vue'),
    PayoutDetailsLoader: () =>
      import('@/components/v2/skeletalLoaders/PayoutDetailsLoader.vue'),
    GenerateReceiptDetails: () =>
      import('@/components/common/GenerateReceiptDetails.vue')
  },
  data () {
    return {
      recurringLogDetails: {},
      showBeneficiaryTable: false,
      showRecurringLogTable: false,
      showBeneficiaryDetails: false,
      showConfirmTerminate: false,
      showConfirmRetry: false,
      showConfirmPayout: false,
      showVerifyAuthDialog: false,
      showInitiatorDrawer: false,
      authState: 'terminateBulkPayout',
      terminateType: '',
      actionType: '',
      payoutStatus: true,
      payload: {
        page: 1,
        size: 10,
        id: ''
      },
      recurringLogPayload: {
        bulkPayoutId: '',
        page: 1,
        size: 10
      },
      isLoading: false,
      isLoadingTable: false,
      reason: '',
      twoFaCode: '',
      isLogTableView: false
    }
  },
  computed: {
    hasIntermediaryBankDetails () {
      return this.$store.state.bulkPayout?.currentBulkPayoutDetails
        ?.attributes[0]?.details?.intermediaryBank
    },
    hasInitiatorDetails () {
      return (
        !this.isRecurringPayout && Object.keys(this.initiatorInfo).length > 0
      )
    },
    transactionInitiatorDetails () {
      return [
        {
          label: 'Name',
          value: `${this.initiatorInfo.firstName || '-'}  ${
            this.initiatorInfo.lastName || '-'
          }`
        },
        {
          label: 'Email Address',
          value: this.initiatorInfo.email || '--'
        },
        {
          label: 'Time Initiated',
          value: this.bulkPayoutDetails.createdAt
            ? this.$dayjs(this.bulkPayoutDetails.createdAt).format(
                'DD MMM YYYY, h:mm A'
              )
            : '--'
        }
      ]
    },
    rate () {
      return this.exRate.find(
        (rate) =>
          rate.to === this.bulkPayoutDetails.currency && rate.from === 'USD'
      )?.exchange_rate
    },
    usdAmountLimit () {
      if (this.hasTransactionLimit) {
        const currency = this.bulkPayoutDetails.currency
        const fee = this.bulkPayoutDetails.fee?.amount || 0

        let amount
        if (currency === 'USD') {
          amount = this.bulkPayoutDetails.totalAmount + fee
        } else {
          amount = (this.bulkPayoutDetails.totalAmount + fee) / this.rate
        }

        return amount - this.hasTransactionLimit
      } else {
        return 0
      }
    },
    isApproverAccess () {
      if (this.hasApproverAccess) {
        if (this.usdAmountLimit > 0) {
          return true
        } else {
          return false
        }
      } else {
        return true
      }
    },
    isApproveTooltipDisabled () {
      if (this.hasApproverAccess) {
        if (this.usdAmountLimit > 0) {
          return false
        } else {
          return true
        }
      } else {
        return false
      }
    },
    approveTooltipContent () {
      const amount =
        this.bulkPayoutDetails.currency === 'USD'
          ? this.usdAmountLimit
          : this.usdAmountLimit * this.rate

      return this.usdAmountLimit > 0
        ? `Your transaction limit is insufficient to approve this transfer by ${this.parseMoneyAll(
            amount,
            this.bulkPayoutDetails.currency
          )}`
        : 'You do not have the access to approve this transfer'
    },
    intermediaryBankDetailsStruct () {
      return [
        {
          title: 'Bank Name',
          value:
            this.$store.state.bulkPayout?.currentBulkPayoutDetails
              ?.attributes[0]?.details?.intermediaryBank.bankName
        },
        {
          title: 'Bank Reference',
          value:
            this.$store.state.bulkPayout?.currentBulkPayoutDetails
              ?.attributes[0]?.details?.intermediaryBank.reference
        },
        {
          title: 'Swift Code/Routing No',
          value:
            this.$store.state.bulkPayout?.currentBulkPayoutDetails
              ?.attributes[0]?.details?.intermediaryBank.swiftCode
        },
        {
          title: 'Bank Address',
          value:
            this.$store.state.bulkPayout?.currentBulkPayoutDetails
              ?.attributes[0]?.details?.intermediaryBank.bankAddress
        }
      ]
    },
    singleBeneficiaryDetails () {
      return [
        {
          title: 'Beneficiary',
          value: this.singlePayoutDetails.accountName
        },
        {
          title: 'Account Number',
          value: this.singlePayoutDetails.accountNumber
        },
        {
          title: 'Bank Name',
          value: this.singlePayoutDetails.bankName
        },
        {
          title: 'Country',
          value: this.singlePayoutDetails.country
        },
        {
          title: 'Swift Code',
          value: this.singlePayoutDetails.swiftCode
        },
        {
          title: 'Routing Number',
          value: this.singlePayoutDetails.routingNumber
        }
      ]
    },
    isOldRecurringPayout () {
      return this.isRecurringPayout && !this.newBulkPayout
    },
    scheduleDetails () {
      return this.bulkPayoutDetails.scheduleDetails
    },
    isCancelStatus () {
      return (
        this.isVerifying ||
        this.isInitiatedBulkPayout ||
        this.isPendingBulkPayout ||
        this.isOngoingStatus
      )
    },
    hasRecurringLogDetails () {
      return this.recurringLogDetails.status
    },
    isRecurringPayoutFailed () {
      return IS_RECURRING_PAYOUT_RETRY_STATUS.includes(
        this.recurringLogDetails.status
      )
    },
    hasScheduleDetails () {
      return this.bulkPayoutDetails.scheduleDetails.isRunning
    },
    isRecurringPayout () {
      return this.bulkPayoutDetails.isRecurring
    },
    singlePayoutDetails () {
      return (
        this.$store.state.bulkPayout?.currentBulkPayoutDetails?.attributes[0]
          ?.details.bankDetails ||
        this.$store.state.bulkPayout?.currentBulkPayoutDetails?.attributes[0]
          ?.details ||
        {}
      )
    },
    newBulkPayout () {
      return this.bulkPayoutID === 'new-bulk-payout'
    },
    isBulkPayoutCancelled () {
      return this.bulkPayoutDetails.status === this.$STATUS.CANCELLED
    },
    isSingleBulkPayout () {
      return this.bulkPayoutDetails.payoutType === 'single'
    },
    isMultipleBulkPayout () {
      return this.bulkPayoutDetails.payoutType === 'multiple'
    },
    isVerifying () {
      return this.bulkPayoutDetails.status === this.$STATUS.VERIFYING
    },
    isInitiatedBulkPayout () {
      return this.bulkPayoutDetails.status === this.$STATUS.INITIATED
    },
    isPendingBulkPayout () {
      return this.bulkPayoutDetails.status === this.$STATUS.PENDING
    },
    isOngoingStatus () {
      return this.bulkPayoutDetails.status === this.$STATUS.ONGOING
    },
    isDisapprovedStatus () {
      return this.bulkPayoutDetails.status === this.$STATUS.DISAPPROVED
    },
    isAwaitingApprovalStatus () {
      return (
        this.bulkPayoutDetails.status ===
        this.$STATUS.AWAITING_BUSINESS_APPROVAL
      )
    },
    bulkPayoutID () {
      return this.$route.params.id
    },
    bulkPayoutDetails () {
      return this.$store.state.bulkPayout.currentBulkPayoutDetails.items
    },
    beneficiaryTableData () {
      return (
        this.$store.state.bulkPayout.currentBulkPayoutDetails.attributes || []
      )
    },
    bulkPayoutLogTableData () {
      return this.$store.state.bulkPayout.bulkPayoutLogs.items || []
    },
    bulkPayoutLogPage () {
      return this.$store.state.bulkPayout.bulkPayoutLogs.page || {}
    },
    page () {
      return this.$store.state.bulkPayout.currentBulkPayoutDetails.page || {}
    },
    selectedMultiBeneficiries () {
      return this.$store.state.bulkPayout.selectedBeneficiaries || []
    },
    savedPayoutDetails () {
      return this.$store.state.bulkPayout.currentBulkPayoutDetails || []
    },
    isSingleCompletedPayout () {
      return (
        this.bulkPayoutDetails.status === this.$STATUS.COMPLETED &&
        this.isSingleBulkPayout
      )
    },
    fileName () {
      return `${this.businessName || this.adminBusinessName} - (${
        this.bulkPayoutDetails.totalAmount
      } ${this.bulkPayoutDetails.currency})`
    }
  },
  methods: {
    parseRelativeTime (val) {
      return ParseRelativeTime(val)
    },
    toggleTableView (row) {
      this.isLogTableView = true
      this.recurringLogDetails = row
      this.showBeneficiaryTable = true
      this.showRecurringLogTable = false
    },
    async getBulkPayoutLogs () {
      this.isLoading = true
      this.recurringLogPayload.bulkPayoutId = this.bulkPayoutID
      const payload = {
        bulkPayoutId: this.recurringLogPayload.bulkPayoutId,
        page: this.recurringLogPayload.page,
        size: this.recurringLogPayload.size
      }
      const vm = this
      await this.$store
        .dispatch('bulkPayout/getBulkPayoutLogs', payload)
        .then(() => {
          this.isLoading = false
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    openInitiatorDrawer () {
      this.showInitiatorDrawer = true
    },
    closeInitiatorDrawer () {
      this.showInitiatorDrawer = false
    },
    newBulkPayoutHandler () {
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 300)
    },
    approveBulkPayout (twoFaCode) {
      this.twoFaCode = twoFaCode.code
      this.actionType = 'approve'
      this.approveTerminateBulkPayoutHandler()
    },
    terminateBulkPayout (twoFaCode) {
      this.twoFaCode = twoFaCode.code

      if (this.terminateType === 'terminateBeforeApproval') {
        this.actionType = 'disapprove'
        this.approveTerminateBulkPayoutHandler()
      } else {
        this.terminateBulkPayoutHandler()
      }
    },
    async confirmBulkPayoutHandler (twoFaCode) {
      this.twoFaCode = twoFaCode.code
      if (this.selectedMultiBeneficiries.length > 0) {
        await this.initiatePayout()
      } else {
        await this.initiateBulkPayout()
      }
      this.closeDialog('showVerifyAuthDialog')
    },
    handleTerminateBulkPayout (reason) {
      this.reason = reason
      this.closeDialog('showConfirmTerminate')
      this.showVerifyAuthDialog = true
    },
    handleConfirmBulkPayout () {
      this.authState = 'confirmBulkPayout'
      this.closeDialog('showConfirmPayout')
      this.showVerifyAuthDialog = true
    },
    handleRetryBulkPayout () {
      this.authState = 'retryBulkPayout'
      this.closeDialog('showConfirmRetry')
      this.showVerifyAuthDialog = true
    },
    async retryBulkPayoutHandler (twoFaCode) {
      this.twoFaCode = twoFaCode.code
      await this.retryBulkPayout()
      this.closeDialog('showVerifyAuthDialog')
    },
    confirmTerminateHandler (type) {
      this.terminateType = type
      this.showConfirmTerminate = true
    },
    confirmApprovePayoutHandler () {
      this.authState = 'approveBulkPayout'
      this.showVerifyAuthDialog = true
    },
    confirmRetryPayoutHandler () {
      this.showConfirmRetry = true
    },
    async retryBulkPayout () {
      this.isLoading = true
      const logId = this.recurringLogDetails.id
      const payload = {
        twoFaCode: this.twoFaCode
      }
      const vm = this
      await this.$store
        .dispatch('bulkPayout/retryBulkPayout', { logId, payload })
        .then(() => {
          this.isLoading = false
          vm.$message({
            showClose: true,
            message: 'Retried successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
          this.closeDialog('showVerifyAuthDialog')
        })
    },
    async terminateBulkPayoutHandler () {
      this.isLoading = true
      const payload = {
        bulkPayoutID: this.bulkPayoutDetails.id,
        reason: this.reason,
        twoFaCode: this.twoFaCode
      }
      const vm = this
      await this.$store
        .dispatch('bulkPayout/cancelBulkPayout', payload)
        .then(() => {
          this.isLoading = false
          this.getBulkPayout('mounted')
          vm.$message({
            showClose: true,
            message: 'Terminated successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
          this.closeDialog('showVerifyAuthDialog')
        })
    },
    async approveTerminateBulkPayoutHandler () {
      const payload = {
        referenceID: this.bulkPayoutDetails.id,
        transactionType: this.$transactionType.PAYOUT,
        action: this.actionType === 'approve' ? 'approve' : 'disapprove',
        cancelReason: this.actionType === 'disapprove' ? this.reason : '',
        twoFaCode: this.twoFaCode
      }

      this.isLoading = true
      const vm = this
      await this.$store
        .dispatch('bulkPayout/approveDisapproveBulkPayout', payload)
        .then(() => {
          this.isLoading = false
          this.getBulkPayout('mounted')
          vm.$message({
            showClose: true,
            message:
              this.actionType === 'approve'
                ? 'Approved Successfully'
                : 'Terminated Successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
          this.closeDialog('showVerifyAuthDialog')
        })
    },
    async getBulkPayout (caller) {
      const functionCaller =
        caller === 'mounted' ? 'isLoading' : 'isLoadingTable'
      this[functionCaller] = true
      this.payload.id = this.bulkPayoutID
      const vm = this
      await this.$store
        .dispatch('bulkPayout/getBulkPayout', this.payload)
        .then(() => {
          this[functionCaller] = false
        })
        .catch((error) => {
          this[functionCaller] = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this[functionCaller] = false
        })
    },
    async initiatePayout () {
      const selectedBeneficiaries = this.savedPayoutDetails.attributes.map(
        (item) => ({
          amount: item.amount.amount,
          beneficiaryID: item.id
        })
      )
      const payload = {
        twoFaCode: this.twoFaCode,
        currency: this.savedPayoutDetails.items.currency,
        remarks: this.savedPayoutDetails.items.remarks,
        beneficiaryType: this.savedPayoutDetails.items.beneficiaryType,
        isRecurring: this.savedPayoutDetails.items.isRecurring,
        amount: this.savedPayoutDetails.items.totalAmount,
        payoutType: this.savedPayoutDetails.items.payoutType,
        scheduleDetails: this.savedPayoutDetails.items.scheduleDetails,
        accounts: selectedBeneficiaries
      }

      this.showValidationDialog = true
      const vm = this
      await this.$store
        .dispatch('bulkPayout/initiateSinglePayout', payload)
        .then(() => {
          this.$store.dispatch(
            'bulkPayout/setActiveComponent',
            'bulk-payout-overview'
          )
          this.$store.dispatch('bulkPayout/removeSelectedBeneficiaries', [])
          this.$router.push('/payout')
          vm.$message({
            showClose: true,
            message: 'Confirmed successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.showValidationDialog = false
        })
    },
    async initiateBulkPayout () {
      this.showValidationDialog = true
      const vm = this
      const formData = this.$store.state.bulkPayout.bulkPayoutFormData
      formData.delete('twoFaCode')
      formData.append('twoFaCode', this.twoFaCode)
      await this.$store
        .dispatch('bulkPayout/initiateBulkPayout', formData)
        .then(() => {
          this.$store.dispatch(
            'bulkPayout/setActiveComponent',
            'bulk-payout-overview'
          )
          this.$router.push('/payout')
          vm.$message({
            showClose: true,
            message: 'Confirmed successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.showValidationDialog = false
        })
    },
    async toggleRecurringBulkPayoutHandler () {
      this.isLoading = true
      const payload = {
        bulkPayoutID: this.bulkPayoutDetails.id
      }
      const vm = this
      await this.$store
        .dispatch('bulkPayout/toggleRecurringBulkPayout', payload)
        .then(() => {
          this.isLoading = false
          this.getBulkPayout('mounted')
          vm.$message({
            showClose: true,
            message: 'Recurring BulkPayout Toggled successfully!',
            type: 'success'
          })
        })
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeDialog (arg) {
      this[arg] = false
    },
    confirmPayoutHandler () {
      this.showConfirmPayout = true
    },
    maskID (id) {
      return MaskID(id)
    },
    handleCurrentChange (payload) {
      this.payload.page = payload.page
      this.getBulkPayout('table')
    },
    handleRecurringLogCurrentChange (payload) {
      this.recurringLogPayload.page = payload.page
      this.getBulkPayoutLogs()
    },
    goBack () {
      this.$router.go(-1)
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    statusClass (status) {
      return StatusClass(status)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Payout ID copied successfully!'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    refreshHandler () {
      this.getBulkPayout('mounted')
    },
    async getInitiatorInfo () {
      await this.$store
        .dispatch(
          'auth/getTransactionInitiator',
          this.bulkPayoutDetails.actorID
        )
        .then(() => {})
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
    },
    generateReceipt () {
      this.payload.id =
        this.$store.state.bulkPayout?.currentBulkPayoutDetails?.attributes[0].id
      this.getReceiptDetails(this.payload)
    },
    sortHandler (sortObj) {
      if (sortObj.sortFormat === 'amount') {
        this.$store.dispatch(
          'bulkPayout/sortBeneficiariesAmountData',
          sortObj.direction
        )
      } else if (sortObj.sortFormat === 'name') {
        this.$store.dispatch(
          'bulkPayout/sortBeneficiariesNameData',
          sortObj.direction
        )
      }
    }
  },
  async mounted () {
    if (!this.newBulkPayout) {
      await this.getBulkPayout('mounted')
      await this.getBulkPayoutLogs()
      if (this.isRecurringPayout) {
        this.payoutStatus = this.hasScheduleDetails
      }
    } else {
      this.newBulkPayoutHandler()
    }
    // dynamically rendering tables :(on mounted this.showBeneficiaryTable should render
    // if it is a newBulkPayout or is not a recurring payout and
    // showRecurringLogTable should render if it is a recurring payout)
    if (this.newBulkPayout || !this.isRecurringPayout) {
      if (this.bulkPayoutDetails.count === 1) {
        this.showBeneficiaryDetails = true
      } else {
        this.showBeneficiaryTable = true
      }
    } else if (this.isRecurringPayout) {
      this.showRecurringLogTable = true
    }
    if (this.isAwaitingApprovalStatus) {
      this.getInitiatorInfo()
    }
    console.log(this.bulkPayoutDetails)
  }
}
</script>

<style>
>>> .el-switch.is-checked .el-switch__core {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}

.el-switch.is-checked .el-switch__core {
  border-color: var(--color-primary) !important;
  background-color: var(--color-primary) !important;
}
>>> .el-switch.is-disabled .el-switch__core {
  cursor: pointer;
}

.el-switch__core {
  background: var(--foreground-on-disabled);
  border-color: var(--foreground-on-disabled);
  width: 48px;
  border-radius: 24px;
  height: 21.5px;
}

>>> .el-switch.is-checked .el-switch__core {
  background: var(--color-primary);
  border-color: var(--color-primary);
}

>>> .el-switch__core:after {
  width: 20px;
  height: 20px;
}

>>> .el-switch.is-checked .el-switch__core::after {
  margin-left: -20px;
}
.approveBtn {
  border: 1px solid #2f855a;
  color: #2f855a;

  &:disabled {
    border: 1px solid #a5a7b6;
    color: #a5a7b6;
  }
}
.terminateBtn {
  border: 1px solid #d80f0f;
  color: #d80f0f;

  &:disabled {
    border: 1px solid #a5a7b6;
    color: #a5a7b6;
  }
}
</style>
